import { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Network, ShieldAlert, SlidersHorizontal, User, UserCog, Users, X, type LucideIcon } from 'lucide-react';
import { ewsDashboardAPI, type DashRichFilterParams } from '../../services/api';
import { downloadTableXlsx, fileSlug } from '../../utils/tableXlsx';
import { InlineLoader } from '../loading';
import { SearchBox } from './DashboardPrimitives';
import { SortTh, useTableSort, type SortAccessors } from './useTableSort';
import EwsTicketsModal from './EwsTicketsModal';
import EwsRemarksModal from './EwsRemarksModal';
import EwsParametersModal from './EwsParametersModal';
import StatTile from './StatTile';
import { useStoreRegions } from '../../hooks/useStoreRegions';
import {
  columnsNotCompared,
  contactLabel,
  ewsColumnHint,
  ewsColumnLabel,
  ewsSheet,
  formatEws,
  joinedLabel,
  managerCodeOf,
  managerRows,
  managerSheet,
  managerTierLabel,
  matchesSearch,
  monthLabel,
  needsConversation,
  placeLabel,
  priorityLabel,
  priorityWhy,
  remarkDay,
  remarksSheet,
  shortMonth,
  signalReason,
  storeKindLabel,
  leftLabel,
  type EwsColumn,
  type EwsData,
  type EwsEmployees,
  type EwsManagerRow,
  type EwsManagerTier,
  type EwsPriority,
  type EwsRemark,
  type EwsRow,
} from './ews';

/** How often the page asks again while a column is still being read, and for how long. */
export const EWS_POLL = { everyMs: 10_000, maxTries: 60 };

/**
 * Dashboard → EWS (Early Warning System).
 *
 * Who may be about to leave. It opens on the At risk list: the people flagged Red or Amber
 * for the finished month picked, with the figures that fired highlighted. The table shows
 * that month; the month before it is read only for the signs to compare against, and a
 * highlighted figure says on hover what it was compared with. All employees shows everyone in view. By Assistant Manager and
 * By Trade Head roll the same people up; a row there opens that manager's people. What
 * fires and who is flagged is decided on the server (ews.logic.ts); this only draws it. A
 * dash is "no figure", never zero: the server sends 0 where 0 is the answer.
 *
 * ONE request feeds the table; search, sort and the Excel download work on the rows it
 * returned, so the file is the table on screen. Unplanned leave, hours and incentive can
 * take minutes on a large project: the server reads them in the background and names them
 * in `pending`, and the page asks again every few seconds, quietly, until they land.
 */

const TH = 'px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500';
// A parameter's column: the month picked, one figure each.
const PARAM_TH = `${TH} border-l border-gray-100 text-center`;
const TD = 'px-2 py-2 text-center text-sm tabular-nums text-gray-700';
const DASH = 'px-2 py-2 text-center text-sm text-gray-300';
const WAITING = 'px-2 py-2 text-center text-sm text-gray-400';
// A ticket count above zero opens the tickets it counts.
const OPENS = 'rounded px-1 font-medium text-primary-700 underline decoration-dotted underline-offset-2 hover:text-primary-900';
const EMP = 'sticky left-0 z-10 bg-white px-3 py-2 text-left';
// Store and Location: one person's, not a month's. The lines inside carry the colours.
const PLACE = 'min-w-[150px] max-w-[220px] px-3 py-2 text-left align-middle';
const AON = 'whitespace-nowrap px-3 py-2 text-left align-middle';
const PRIORITY = 'whitespace-nowrap px-3 py-2 text-center align-middle';
// The figure that fired a warning sign.
const FIRED = 'inline-block rounded bg-rose-50 px-1.5 font-semibold text-rose-700';
const PRIORITY_PILL: Record<EwsPriority, string> = {
  red: 'bg-rose-50 text-rose-700',
  amber: 'bg-amber-50 text-amber-700',
};
const CHIP = 'inline-flex items-center gap-1 rounded-full border border-primary-200 bg-white px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50';

/** The tab strip of the master pages (StoreMasterPage), as every web page draws it. */
function Strip<T extends string>({ label, value, options, onChange }: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string; icon: LucideIcon }>;
  onChange: (value: T) => void;
}) {
  return (
    <div role="tablist" aria-label={label} className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
          className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${value === o.value ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
        >
          <o.icon className="h-4 w-4" />
          {o.label}
        </button>
      ))}
    </div>
  );
}

type EwsWho = 'risk' | 'all';
type EwsBy = 'people' | EwsManagerTier;
type EwsQuick = EwsPriority | 'noConversation';

const QUICK_LABEL: Record<EwsQuick, string> = { red: 'Red', amber: 'Amber', noConversation: 'No conversation yet' };
const PRIORITY_RANK: Record<EwsPriority, number> = { red: 2, amber: 1 };

export default function EwsTab({ projectId, month, employees = 'active', roleId, filters }: {
  projectId: number | null;
  /** The last month of the window, 'YYYY-MM'. */
  month: string;
  /** Who is listed: working today (the default), left, or both. */
  employees?: EwsEmployees;
  roleId?: string;
  filters?: DashRichFilterParams;
}) {
  const [data, setData] = useState<EwsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [openTickets, setOpenTickets] = useState<{ code: string; name: string; month: string; count: number } | null>(null);
  const [openRemarks, setOpenRemarks] = useState<{ code: string; name: string } | null>(null);
  const [downloadNote, setDownloadNote] = useState<string | null>(null);
  const [who, setWho] = useState<EwsWho>('risk');
  const [by, setBy] = useState<EwsBy>('people');
  const [quick, setQuick] = useState<EwsQuick | null>(null);
  const [pick, setPick] = useState<{ tier: EwsManagerTier; code: string | null; name: string } | null>(null);
  const [choosing, setChoosing] = useState(false);
  // Read again after the parameters change: the server reads, and flags, only those on.
  const [reload, setReload] = useState(0);
  const reqRef = useRef(0);
  const { regionOf } = useStoreRegions();

  useEffect(() => {
    if (projectId == null) return;
    const reqId = ++reqRef.current;
    // Another set of people: a manager or a count picked on the last one means nothing here.
    setQuick(null);
    setPick(null);
    let timer: ReturnType<typeof setTimeout> | undefined;
    let tries = 0;
    // `quiet`: an ask again while a column is still being read. It keeps the table on
    // screen and says nothing; a failure leaves the table as it was.
    const ask = (quiet: boolean) => {
      if (!quiet) {
        setLoading(true);
        setError(null);
      }
      ewsDashboardAPI
        .getDashboard(projectId, { month, roleId: roleId || undefined, ...(employees !== 'active' ? { employees } : {}), ...filters })
        .then((res) => {
          if (reqId !== reqRef.current) return;
          const next = (res.data?.data ?? null) as EwsData | null;
          setData(next);
          tries += 1;
          if (next && (next.pending ?? []).length > 0 && tries < EWS_POLL.maxTries) {
            timer = setTimeout(() => ask(true), EWS_POLL.everyMs);
          }
        })
        .catch((err) => {
          if (reqId !== reqRef.current) return;
          if (quiet) {
            // One failed ask must not leave "fills in by itself" on screen for good.
            tries += 1;
            if (tries < EWS_POLL.maxTries) timer = setTimeout(() => ask(true), EWS_POLL.everyMs);
            return;
          }
          setError(err.response?.data?.message || 'Failed to load EWS');
          setData(null);
        })
        .finally(() => {
          if (reqId === reqRef.current && !quiet) setLoading(false);
        });
    };
    ask(false);
    return () => {
      // An answer still on its way must not schedule another ask after this.
      reqRef.current += 1;
      if (timer) clearTimeout(timer);
    };
  }, [month, projectId, employees, roleId, reload, JSON.stringify(filters)]); // eslint-disable-line react-hooks/exhaustive-deps

  const months = useMemo(() => data?.months ?? [], [data]);
  const columns = useMemo(() => data?.columns ?? [], [data]);
  // Nobody who has left is flagged: the Left list has only "all employees" to show.
  const riskOffered = employees !== 'left';
  const atRisk = riskOffered && who === 'risk';
  // The people the tiles, the counts on the strip and the manager views are about.
  const inPick = useMemo(
    () => (data?.rows ?? []).filter((r) => !pick || managerCodeOf(r, pick.tier) === pick.code),
    [data, pick],
  );
  const flagged = useMemo(() => inPick.filter((r) => r.priority), [inPick]);
  const shown = useMemo(() => {
    const listed = inPick
      .filter((r) => !atRisk || r.priority)
      .filter((r) => (quick === 'noConversation' ? needsConversation(r) : !quick || r.priority === quick))
      .filter((r) => matchesSearch(r, search));
    if (!atRisk) return listed;
    // Most urgent first, by name within that; a column clicked sorts over this.
    const rank = (r: EwsRow) => (r.priority ? PRIORITY_RANK[r.priority] : 0);
    return [...listed].sort((a, b) => rank(b) - rank(a));
  }, [inPick, atRisk, quick, search]);

  const accessors = useMemo(() => {
    const out: SortAccessors<EwsRow> = {
      name: (r) => r.name,
      priority: (r) => (r.priority ? PRIORITY_RANK[r.priority] : null),
      remarks: (r) => r.remarkCount ?? 0,
      store: (r) => r.store || null,
      location: (r) => placeLabel(r) || null,
      aon: (r) => r.aonDays ?? null,
    };
    const last = months.length - 1;
    for (const col of columns) out[col] = (r) => r.values[col]?.[last] ?? null;
    return out;
  }, [columns, months]);
  const sort = useTableSort(shown, accessors, { firstDir: (key) => (key === 'name' ? 'asc' : 'desc') });

  // Another project may not have these roles: its people are then the only view.
  const tier: EwsManagerTier | null = by === 'people' || !data?.managerTiers?.available ? null : by;
  const managers = useMemo(
    () => (tier ? managerRows(data?.rows ?? [], tier).filter((m) => m.name.toLowerCase().includes(search.trim().toLowerCase())) : []),
    [data, tier, search],
  );
  const managerAccessors = useMemo<SortAccessors<EwsManagerRow>>(() => ({
    name: (m) => m.name,
    team: (m) => m.team,
    red: (m) => m.red,
    amber: (m) => m.amber,
    noConversation: (m) => m.noConversation,
  }), []);
  const managerSort = useTableSort(managers, managerAccessors, { firstDir: (key) => (key === 'name' ? 'asc' : 'desc') });

  const download = async () => {
    if (!data || !projectId) return;
    setDownloading(true);
    setDownloadNote(null);
    try {
      const file = `ews-${fileSlug(months.length > 0 ? monthLabel(months[months.length - 1]) : '')}`;
      if (tier) {
        await downloadTableXlsx(`${file}-by-${tier === 'am' ? 'assistant-manager' : 'trade-head'}`, managerSheet(tier, managerSort.sorted, data.remarksAllowed === true && !data.remarksMissing));
        return;
      }
      const sheets = [ewsSheet(data, sort.sorted, regionOf)];
      if (data.remarksAllowed && !data.remarksMissing) {
        try {
          const res = await ewsDashboardAPI.getRemarks(projectId);
          sheets.push(remarksSheet((res.data?.data?.remarks ?? []) as EwsRemark[], sort.sorted));
        } catch {
          setDownloadNote('Remarks could not be read just now, so this file has no Remarks sheet.');
        }
      }
      await downloadTableXlsx(file, sheets);
    } finally {
      setDownloading(false);
    }
  };

  const pickManager = (m: EwsManagerRow) => {
    if (!tier) return;
    setPick({ tier, code: m.code, name: m.name });
    setBy('people');
    setQuick(null);
    setSearch('');
  };

  // Written down: the row shows it at once, and the next read from the server agrees.
  const remarkAdded = (saved: EwsRemark) => {
    setData((cur) => (cur ? {
      ...cur,
      rows: cur.rows.map((r) => (r.code !== saved.employeeCode ? r : {
        ...r,
        remarkCount: (r.remarkCount ?? 0) + 1,
        lastRemarkAt: saved.at,
        contact: r.contact ? { ...r.contact, state: 'done' as const, hoursLeft: null } : r.contact,
      })),
    } : cur));
  };

  if (projectId == null) return null;
  if (loading && !data) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-8">
        <InlineLoader label="Loading EWS… the first open of these months can take a minute or two" />
      </div>
    );
  }
  if (error) {
    return <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>;
  }
  if (!data) return null;

  const failedNames = data.failed.map((c) => ewsColumnLabel(c, data));
  const pending = new Set(data.pending ?? []);
  const pendingNames = [...pending].map((c) => ewsColumnLabel(c, data));
  const notCompared = columnsNotCompared(data).map((c) => ewsColumnLabel(c, data));
  const remarksShown = data.remarksAllowed === true;
  const lastIndex = months.length - 1;
  const lastMonth = months.length > 0 ? shortMonth(months[lastIndex]) : '';
  const fired = (r: EwsRow, col: EwsColumn) => (r.signals ?? []).includes(col);
  const fixedColumns = remarksShown ? 6 : 5;
  // A count lists its people, whichever view it was clicked from.
  const toggleQuick = (next: EwsQuick) => {
    setBy('people');
    setQuick((cur) => (cur === next && by === 'people' ? null : next));
  };
  const red = flagged.filter((r) => r.priority === 'red').length;
  const amber = flagged.filter((r) => r.priority === 'amber').length;
  const noConversation = flagged.filter(needsConversation).length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {riskOffered && (
          <Strip<EwsWho>
            label="Who is listed"
            value={who}
            onChange={(next) => { setWho(next); setQuick(null); setBy('people'); }}
            options={[
              { value: 'risk', label: `At risk (${flagged.length.toLocaleString('en-IN')})`, icon: ShieldAlert },
              { value: 'all', label: `All employees (${inPick.length.toLocaleString('en-IN')})`, icon: Users },
            ]}
          />
        )}
        {data.managerTiers?.available && (
          <Strip<EwsBy>
            label="View by"
            value={tier ?? 'people'}
            onChange={(next) => { setBy(next); setPick(null); setQuick(null); setSearch(''); }}
            options={[
              { value: 'people', label: 'Employees', icon: User },
              { value: 'am', label: 'By Assistant Manager', icon: UserCog },
              { value: 'th', label: 'By Trade Head', icon: Network },
            ]}
          />
        )}
      </div>

      {riskOffered && (
        <div className={`grid gap-3 ${remarksShown ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
          <StatTile
            size="compact"
            label="Red"
            value={red.toLocaleString('en-IN')}
            helper="Contact within 24 hours"
            hint="Three or more warning signs, or recurring red flags"
            valueTone={red > 0 ? 'bad' : 'default'}
            onClick={red > 0 ? () => toggleQuick('red') : undefined}
          />
          <StatTile
            size="compact"
            label="Amber"
            value={amber.toLocaleString('en-IN')}
            helper="Contact within 72 hours"
            hint="Two warning signs"
            valueTone={amber > 0 ? 'warn' : 'default'}
            onClick={amber > 0 ? () => toggleQuick('amber') : undefined}
          />
          {remarksShown && (
            <StatTile
              size="compact"
              label="No conversation yet"
              value={data.remarksMissing ? '—' : noConversation.toLocaleString('en-IN')}
              helper="Flagged, no remark since"
              hint="Red or Amber, with no remark written since they were flagged"
              valueTone={noConversation > 0 ? 'warn' : 'default'}
              onClick={!data.remarksMissing && noConversation > 0 ? () => toggleQuick('noConversation') : undefined}
            />
          )}
        </div>
      )}

      {(pick || quick) && (
        <div className="flex flex-wrap items-center gap-2">
          {pick && (
            <button type="button" className={CHIP} onClick={() => setPick(null)} aria-label={`Stop showing only ${pick.name}'s team`}>
              {managerTierLabel(pick.tier)}: {pick.name}
              <X className="h-3 w-3" />
            </button>
          )}
          {quick && (
            <button type="button" className={CHIP} onClick={() => setQuick(null)} aria-label={`Stop showing only ${QUICK_LABEL[quick]}`}>
              {QUICK_LABEL[quick]}
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[220px] flex-1">
          <SearchBox value={search} onChange={setSearch} placeholder={tier ? `Search ${managerTierLabel(tier)}` : 'Search name or code'} />
        </div>
        <span className="text-sm text-gray-500">
          {tier
            ? `${managers.length.toLocaleString('en-IN')} ${managerTierLabel(tier)}${managers.length === 1 ? '' : 's'}`
            : `${shown.length.toLocaleString('en-IN')} employee${shown.length === 1 ? '' : 's'}`}
        </span>
        {loading && <InlineLoader label="Updating…" />}
        {data.canChooseColumns && (data.columnChoices ?? []).length > 0 && (
          <button
            type="button"
            onClick={() => setChoosing(true)}
            className="flex h-10 items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-600 shadow-sm transition hover:bg-gray-50"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Parameters
          </button>
        )}
        <button
          type="button"
          onClick={download}
          disabled={downloading || (tier ? managers.length === 0 : shown.length === 0)}
          className="flex h-10 items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-600 shadow-sm transition hover:bg-gray-50 disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          Excel
        </button>
      </div>

      {failedNames.length > 0 && (
        <p className="text-sm text-amber-700">
          {failedNames.join(' and ')} could not be read just now, so {failedNames.length === 1 ? 'it shows' : 'they show'} dashes. Reload to try again.
        </p>
      )}
      {pendingNames.length > 0 && (
        <p className="text-sm text-gray-500">
          {pendingNames.join(' and ')} {pendingNames.length === 1 ? 'is' : 'are'} still being read. {pendingNames.length === 1 ? 'It fills' : 'They fill'} in by {pendingNames.length === 1 ? 'itself' : 'themselves'}.
        </p>
      )}
      {notCompared.length > 0 && (
        <p className="text-sm text-gray-500">
          {notCompared.join(' and ')} can't be compared yet: {notCompared.length === 1 ? 'its' : 'their'} latest figures aren't in, so {notCompared.length === 1 ? 'it adds' : 'they add'} no warning signs for now.
        </p>
      )}
      {data.partial && <p className="text-sm text-amber-700">Names could not be read just now: codes are shown.</p>}
      {data.remarksMissing && <p className="text-sm text-amber-700">Remarks could not be read just now: counts and timers are not shown.</p>}
      {data.managerTiers?.partial && <p className="text-sm text-amber-700">Some reporting lines could not be read just now: a few people may sit under the wrong manager.</p>}
      {downloadNote && <p className="text-sm text-amber-700">{downloadNote}</p>}
      {data.storesMissing && <p className="text-sm text-amber-700">Stores could not be read just now: store and location are blank.</p>}
      {data.joinDatesMissing && (
        <p className="text-sm text-amber-700">Joining dates could not be read just now: a month before someone joined may show 0.</p>
      )}

      {tier ? (
        <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white">
          <table className="min-w-full border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <SortTh label={managerTierLabel(tier)} sortKey="name" sort={managerSort} className={`${TH} px-3`} />
                <SortTh label="Team" sortKey="team" sort={managerSort} align="center" className={TH} />
                <SortTh label="Red" sortKey="red" sort={managerSort} align="center" className={TH} />
                <SortTh label="Amber" sortKey="amber" sort={managerSort} align="center" className={TH} />
                {remarksShown && <SortTh label="No conversation yet" sortKey="noConversation" sort={managerSort} align="center" className={TH} />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {managerSort.sorted.map((m) => (
                <tr key={m.code ?? 'none'} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-left">
                    <button type="button" className={`text-sm ${OPENS}`} title={`Open ${m.name}'s team`} onClick={() => pickManager(m)}>{m.name}</button>
                  </td>
                  <td className={TD}>{m.team.toLocaleString('en-IN')}</td>
                  <td className={TD}>{m.red.toLocaleString('en-IN')}</td>
                  <td className={TD}>{m.amber.toLocaleString('en-IN')}</td>
                  {remarksShown && <td className={TD}>{data.remarksMissing ? '—' : m.noConversation.toLocaleString('en-IN')}</td>}
                </tr>
              ))}
              {managerSort.sorted.length === 0 && (
                <tr>
                  <td colSpan={remarksShown ? 5 : 4} className="px-3 py-8 text-center text-sm text-gray-400">
                    {search.trim() ? 'Nobody matches this search.' : 'No employees in view.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
      <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white">
        <table className="min-w-full border-collapse">
          <thead className="bg-gray-50">
            <tr>
              <SortTh label="Employee" sortKey="name" sort={sort} className={`${TH} sticky left-0 z-20 bg-gray-50 px-3`} />
              <SortTh label="Priority" sortKey="priority" sort={sort} align="center" className={`${TH} px-3`} title="Red: three or more warning signs, or recurring red flags. Amber: two" />
              {remarksShown && <SortTh label="Remarks" sortKey="remarks" sort={sort} align="center" className={`${TH} px-3`} title="The conversations written down about this employee. Red is to be contacted within 24 hours, Amber within 72" />}
              <SortTh label="Store" sortKey="store" sort={sort} className={`${TH} px-3`} title="The store they work at, or last worked at; its trade and category" />
              <SortTh label="Location" sortKey="location" sort={sort} className={`${TH} px-3`} title="The store's city and state; its region" />
              <SortTh label="AON" sortKey="aon" sort={sort} className={`${TH} px-3`} title="Time with the organisation: from the joining date to today, or to the last working day for someone who has left" />
              {columns.map((col) => (
                <SortTh key={col} label={ewsColumnLabel(col, data)} sortKey={col} sort={sort} align="center" className={PARAM_TH} title={ewsColumnHint(col, data)} />
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sort.sorted.map((r) => (
              <tr key={r.code} className="hover:bg-gray-50">
                <td className={EMP}>
                  <div className="text-sm font-medium text-gray-900">{r.name}</div>
                  <div className="text-xs text-gray-400">{r.code}</div>
                  {r.leftOn && <div className="text-xs font-medium text-rose-600">{leftLabel(r.leftOn)}</div>}
                </td>
                <td className={PRIORITY}>
                  {r.priority
                    ? <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${PRIORITY_PILL[r.priority]}`}>{priorityLabel(r.priority)}</span>
                    : <span className="text-sm text-gray-300">—</span>}
                  {priorityWhy(r) && <div className="mt-0.5 text-xs text-gray-400">{priorityWhy(r)}</div>}
                </td>
                {remarksShown && (
                  <td className={PRIORITY}>
                    {data.remarksMissing ? <span className="text-sm text-gray-300">—</span> : (
                      <button
                        type="button"
                        className={`text-sm ${OPENS}`}
                        title={`${r.name}'s remarks`}
                        onClick={() => setOpenRemarks({ code: r.code, name: r.name })}
                      >
                        {r.remarkCount ? `${r.remarkCount} · ${remarkDay(r.lastRemarkAt)}` : 'Add'}
                      </button>
                    )}
                    {contactLabel(r.contact) && <div className="mt-0.5 text-xs font-medium text-rose-600">{contactLabel(r.contact)}</div>}
                  </td>
                )}
                <td className={PLACE}>
                  {r.store ? (
                    <>
                      <div className="text-sm text-gray-700">{r.store}</div>
                      {storeKindLabel(r) && <div className="text-xs text-gray-400">{storeKindLabel(r)}</div>}
                    </>
                  ) : <div className="text-sm text-gray-300">—</div>}
                </td>
                <td className={PLACE}>
                  {placeLabel(r) ? (
                    <>
                      <div className="text-sm text-gray-700">{placeLabel(r)}</div>
                      {r.state && regionOf(r.state) && <div className="text-xs text-gray-400">{regionOf(r.state)}</div>}
                    </>
                  ) : <div className="text-sm text-gray-300">—</div>}
                </td>
                <td className={AON}>
                  {r.aonLabel ? (
                    <>
                      <div className="text-sm tabular-nums text-gray-700">{r.aonLabel}</div>
                      <div className="text-xs text-gray-400">{joinedLabel(r.joinedOn)}</div>
                    </>
                  ) : <div className="text-sm text-gray-300">—</div>}
                </td>
                {columns.map((col) => {
                  const ym = months[lastIndex];
                  const v = r.values[col]?.[lastIndex] ?? null;
                  if (pending.has(col)) return <td key={col} className={WAITING}>…</td>;
                  const hit = fired(r, col);
                  if (col === 'tickets' && v != null && v > 0) {
                    return (
                      <td key={col} className={TD}>
                        <span className={hit ? FIRED : undefined} title={hit ? signalReason(col, r, months) : undefined}>
                          <button
                            type="button"
                            className={OPENS}
                            title={`Open ${r.name}'s tickets of ${shortMonth(ym)}`}
                            onClick={() => setOpenTickets({ code: r.code, name: r.name, month: ym, count: v })}
                          >
                            {formatEws(col, v)}
                          </button>
                        </span>
                      </td>
                    );
                  }
                  const full = col === 'hours' ? r.fullDays?.[lastIndex] ?? null : null;
                  return (
                    <td key={col} className={v == null ? DASH : TD}>
                      {hit ? <span className={FIRED} title={signalReason(col, r, months)}>{formatEws(col, v)}</span> : formatEws(col, v)}
                      {full != null && <div className="whitespace-nowrap text-[11px] text-gray-400">{full} day{full === 1 ? '' : 's'} 9h+</div>}
                    </td>
                  );
                })}
              </tr>
            ))}
            {sort.sorted.length === 0 && (
              <tr>
                <td colSpan={fixedColumns + columns.length} className="px-3 py-8 text-center text-sm text-gray-400">
                  {search.trim()
                    ? 'Nobody matches this search.'
                    : atRisk ? `Nobody is flagged for ${lastMonth}.` : 'No employees in view.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {choosing && (
        <EwsParametersModal
          projectId={projectId}
          choices={data.columnChoices ?? []}
          onSaved={() => { setChoosing(false); setReload((n) => n + 1); }}
          onClose={() => setChoosing(false)}
        />
      )}
      {openRemarks && (
        <EwsRemarksModal
          projectId={projectId}
          employee={openRemarks}
          onAdded={remarkAdded}
          onClose={() => setOpenRemarks(null)}
        />
      )}
      {openTickets && (
        <EwsTicketsModal
          projectId={projectId}
          employee={{ code: openTickets.code, name: openTickets.name }}
          month={openTickets.month}
          count={openTickets.count}
          onClose={() => setOpenTickets(null)}
        />
      )}
    </div>
  );
}
